import React, { useState, useEffect } from 'react';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const START_YEAR = 2024;
const START_MONTH = 5; // June (0-indexed)
const END_YEAR = 2025;
const END_MONTH = 5; // June (0-indexed)

const Calendar = ({ dates, selectedDate, onDateSelect, dateToEntries }) => {
  const [year, setYear] = useState(START_YEAR);
  const [month, setMonth] = useState(START_MONTH);

  // Sync calendar view to the selected date's month
  useEffect(() => {
    if (selectedDate) {
      const [y, m] = selectedDate.split('-').map(Number);
      setYear(y);
      setMonth(m - 1);
    }
  }, [selectedDate]);

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();

  const blanks = Array(firstDay).fill(null);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const canGoBack = year > START_YEAR || (year === START_YEAR && month > START_MONTH);
  const canGoForward = year < END_YEAR || (year === END_YEAR && month < END_MONTH);

  const goBack = () => {
    if (!canGoBack) return;
    if (month === 0) {
      setMonth(11);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const goForward = () => {
    if (!canGoForward) return;
    if (month === 11) {
      setMonth(0);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  const formatDate = (day) => {
    const m = String(month + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    return `${year}-${m}-${d}`;
  };

  const entryDates = new Set(dates);

  return (
    <div className="calendar">
      <div className="calendar-header">
        <button
          className="calendar-nav-btn"
          onClick={goBack}
          disabled={!canGoBack}
          aria-label="Previous month"
        >
          &lsaquo;
        </button>
        <h2>{MONTH_NAMES[month]} {year}</h2>
        <button
          className="calendar-nav-btn"
          onClick={goForward}
          disabled={!canGoForward}
          aria-label="Next month"
        >
          &rsaquo;
        </button>
      </div>
      <div className="calendar-grid">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((name) => (
          <div key={name} className="calendar-day-name">{name}</div>
        ))}
        {blanks.map((_, i) => (
          <div key={`blank-${i}`} className="calendar-day empty"></div>
        ))}
        {days.map((day) => {
          const dateString = formatDate(day);
          const isSelected = selectedDate === dateString;
          const hasEntry = entryDates.has(dateString);

          return (
            <div
              key={day}
              className={`calendar-day${hasEntry ? ' has-entry' : ''}${isSelected ? ' selected' : ''}`}
              onClick={() => hasEntry && onDateSelect(dateString)}
              role={hasEntry ? 'button' : undefined}
              tabIndex={hasEntry ? 0 : undefined}
              onKeyDown={(e) => {
                if (hasEntry && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onDateSelect(dateString);
                }
              }}
            >
              {day}
              {hasEntry && dateToEntries && dateToEntries[dateString] && dateToEntries[dateString].length > 1 && (
                <span className="multi-entry-badge">{dateToEntries[dateString].length}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Calendar;
